import moment from "moment";

import { convertIntToMoney } from "../../helpers/conversions";

// Helpers do módulo de relatórios (BDM-1040).

export const CHART_TYPES = [
    { label: 'Barras', value: 'bar' },
    { label: 'Linha', value: 'line' },
    { label: 'Rosca', value: 'doughnut' },
    { label: 'Sem gráfico', value: 'none' },
];

export const ORDER_DIRECTIONS = [
    { label: 'Crescente', value: 'asc' },
    { label: 'Decrescente', value: 'desc' },
];

/**
 * O operador sai do TIPO do campo, não de uma escolha do usuário: lista
 * escolhe entre valores, texto procura trecho, data define um intervalo. Um
 * select de operador a mais era o que deixava a tela confusa.
 */
export const filterOperatorFor = field => {
    if (!field) return 'eq';
    if (field.options || field.reference) return 'in';
    if (field.type === 'date') return 'between';
    if (field.type === 'text') return 'contains';

    return 'eq';
};

export const emptyFilterValue = operator => {
    if (operator === 'in') return [];
    if (operator === 'between') return { from: null, to: null };

    return '';
};

/**
 * Filtro salvo antes de o operador ser derivado do tipo (os prontos usam
 * `eq` com um valor só) passa a caber no controle de hoje sem perder nada.
 */
export const normalizeFilter = (filter, field) => {
    const operator = filterOperatorFor(field);

    if (operator === 'in' && filter.operator !== 'in') {
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];

        return { ...filter, operator: 'in', value: values.filter(value => value !== '' && value !== null) };
    }

    if (operator === 'between' && filter.operator !== 'between') {
        return {
            ...filter,
            operator: 'between',
            value: {
                from: ['eq', 'gte'].includes(filter.operator) ? filter.value : null,
                to: ['eq', 'lte'].includes(filter.operator) ? filter.value : null,
            },
        };
    }

    return filter;
};

/** Texto do filtro para a tela do relatório: "Status: Em aberto, Parcial". */
export const describeFilter = (filter, field) => {
    const label = field?.label ?? filter.field;
    const optionLabel = value => field?.options?.find(option => option.value === value)?.label ?? value;
    const date = value => value ? moment(value).format('DD/MM/YYYY') : null;

    if (filter.operator === 'between') {
        const from = date(filter.value?.from);
        const to = date(filter.value?.to);
        const text = from && to ? `de ${from} até ${to}` : from ? `a partir de ${from}` : to ? `até ${to}` : null;

        return text ? { label, value: text } : null;
    }

    if (filter.operator === 'contains') {
        return filter.value ? { label, value: `contém "${filter.value}"` } : null;
    }

    const values = (Array.isArray(filter.value) ? filter.value : [filter.value]).filter(value => value !== '' && value !== null);

    return values.length ? { label, value: values.map(optionLabel).join(', ') } : null;
};

// As referências que o construtor sabe resolver num select de cadastro.
export const REFERENCE_ENDPOINTS = {
    finance_category: '/finance-category',
    finance_cost_center: '/finance-cost-center',
    finance_bank_account: '/finance-bank-account',
    billing_company: '/finance-billing-company',
};

export const defaultPeriod = () => ({
    start_date: moment().startOf('month').format('YYYY-MM-DD'),
    end_date: moment().endOf('month').format('YYYY-MM-DD'),
});

export const defaultConfig = dataset => ({
    date_field: dataset?.default_date_field ?? null,
    ...defaultPeriod(),
    columns: [],
    group_by: [],
    filters: [],
    order_by: null,
    chart: { type: 'bar' },
});

export const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') return '—';

    if (type === 'money') return convertIntToMoney(value, true);
    if (type === 'number') return new Intl.NumberFormat('pt-BR').format(value);
    if (type === 'date') return moment(value).format('DD/MM/YYYY');

    return value;
};

export const isMeasure = column => column.role === 'measure';

// Rosca compara partes de um todo e as fatias pequenas viram ruído; barra e
// linha aguentam mais categorias antes do eixo ficar ilegível.
const CHART_ROW_LIMIT = { doughnut: 8, default: 20 };

/**
 * O gráfico sai do FORMATO do resultado, não de uma escolha do usuário: a
 * primeira dimensão vira o eixo e cada métrica vira uma série. Sem dimensão
 * agrupada não há o que desenhar — a tabela detalhada já é a resposta.
 *
 * Devolve `shown`/`total` porque o corte precisa ser dito na tela: 20 barras
 * caladas em cima de uma tabela de 150 linhas passam por "é isso que existe".
 */
export const buildChartData = (result, type) => {
    if (!result || type === 'none' || !result.grouped) return null;

    const dimension = result.columns.find(column => !isMeasure(column));
    const measures = result.columns.filter(isMeasure);

    if (!dimension || measures.length === 0 || result.rows.length === 0) return null;

    const limit = CHART_ROW_LIMIT[type] ?? CHART_ROW_LIMIT.default;
    const series = type === 'doughnut' ? measures.slice(0, 1) : measures;

    // Quando corta, o gráfico corta pelos MAIORES da primeira métrica, e não
    // pela ordem da tabela: ordenada por nome, ela entregaria 20 linhas
    // quaisquer com cara de ranking. Dimensão com ordem própria (mês, data)
    // fica fora disso — ordenada por valor a linha do tempo deixa de ser uma.
    const ranked = dimension.ordinal || result.rows.length <= limit
        ? result.rows
        : [...result.rows].sort((a, b) => Number(b[series[0].key] ?? 0) - Number(a[series[0].key] ?? 0));

    const rows = ranked.slice(0, limit);

    return {
        labels: rows.map(row => String(row[dimension.key] ?? '—')),
        measures: series,
        values: series.map(measure => rows.map(row => Number(row[measure.key] ?? 0))),
        moneyScale: series.every(measure => measure.type === 'money'),
        ordinal: Boolean(dimension.ordinal),
        shown: rows.length,
        total: result.rows.length,
    };
};

export const reportFileName = name => {
    const slug = (name || 'relatorio')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

    return `${slug || 'relatorio'}-${moment().format('YYYY-MM-DD')}.xlsx`;
};

/**
 * O backend devolve o erro como Blob quando a resposta foi pedida assim —
 * sem desempacotar, a mensagem chega como "[object Blob]".
 */
export const parseBlobError = async error => {
    const data = error?.response?.data;

    if (!(data instanceof Blob)) return error;

    try {
        const parsed = JSON.parse(await data.text());
        error.response.data = parsed;
    } catch (parseError) {
        error.response.data = { message: 'Não foi possível gerar o arquivo' };
    }

    return error;
};
